import clsx from 'clsx'

import React, { useEffect, useRef, useState } from 'react'
import DragAndDropMove from 'components/ui/drag-and-drop-move'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'
import Canvas from './canvas'
import Zoom from './zoom'
import styles from './index.module.css'

type BaseProps = {
  src: string
  rounded?: boolean
  onClose: () => void
  onApply: (result: unknown) => void
  withImageRecommendations?: boolean
}

/**
 * Props for the CroppingTool component.
 *
 * There are two ways to specify the crop overlay dimensions:
 *
 * 1. **Explicit dimensions (recommended)**: Use `overlayWidth` and `overlayHeight` together.
 *    Both must be provided as numbers representing pixel values.
 *    ```tsx
 *    <CroppingTool overlayWidth={600} overlayHeight={200} ... />
 *    ```
 *
 * 2. **Style object (deprecated)**: Use `overlayStyle` with width/height in the style object.
 *    ```tsx
 *    <CroppingTool overlayStyle={{ width: 600, height: 200 }} ... />
 *    ```
 *
 * The `never` types enforce that you cannot mix these approaches - use one or the other.
 */
type Props = BaseProps &
  (
    | {
        overlayStyle: React.CSSProperties
        overlayWidth?: never
        overlayHeight?: never
      }
    | { overlayStyle?: never; overlayWidth: number; overlayHeight: number }
  )
const ZOOM_STEP = 0.01
const defaultPos = {
  top: 0,
  left: 0,
}
const defaultSize = {
  w: 0,
  h: 0,
}

const parsePixelValue = (value: string | number | undefined): number => {
  if (typeof value === 'number') return value
  return +String(value || '0').replace('px', '')
}

const calcZoomOptions = ({
  overlayStyle,
  imageSize,
}: {
  overlayStyle: React.CSSProperties
  imageSize: { w: number; h: number }
}) => {
  const width = parsePixelValue(overlayStyle.width)
  const height = parsePixelValue(overlayStyle.height)
  let min = Math.max(width / imageSize.w, height / imageSize.h)
  return {
    min,
    max: 2,
    step: ZOOM_STEP,
  }
}

/**
 * An image cropping tool with zoom, drag, and crop functionality in a modal interface
 * @param src - The source URL of the image to be cropped
 * @param overlayStyle - Style object defining the crop overlay dimensions and appearance (deprecated, use overlayWidth/overlayHeight)
 * @param overlayWidth - Width of the crop overlay in pixels
 * @param overlayHeight - Height of the crop overlay in pixels
 * @param rounded - Whether to show rounded corners on the crop overlay - defaults to false
 * @param onClose - Callback function called when the modal is closed
 * @param onApply - Callback function called when the crop is applied
 * @param withImageRecommendations - Whether to show image recommendations - defaults to false
 * @returns A modal window containing the image cropping interface with zoom and drag controls
 */
const CroppingTool = ({
  src,
  overlayStyle: overlayStyleProp,
  overlayWidth,
  overlayHeight,
  rounded,
  onClose,
  onApply,
  withImageRecommendations,
}: Props): React.ReactNode => {
  // Support both overlayStyle prop and explicit width/height props
  const overlayStyle: React.CSSProperties = overlayStyleProp ?? {
    width: overlayWidth,
    height: overlayHeight,
  }
  const [position, setPosition] = useState(defaultPos)
  const [imageSize, setImageSize] = useState(defaultSize)
  const [imageContainerSize, setImageContainerSize] = useState(defaultSize)
  const [zoom, setZoom] = useState(1)
  const [zoomOptions, setZoomOptions] = useState<{
    min: number
    max: number
    step: number
  } | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const imageContainerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (imageRef.current) {
      setImageSize({
        w: imageRef.current.offsetWidth,
        h: imageRef.current.offsetHeight,
      })
    }

    if (imageContainerRef.current) {
      setImageContainerSize({
        w: imageContainerRef.current.offsetWidth,
        h: imageContainerRef.current.offsetHeight,
      })
    }
  }, [imageRef.current, imageContainerRef.current, zoom])
  useEffect(() => {
    if (!zoomOptions && imageSize.w) {
      setZoomOptions(
        calcZoomOptions({
          overlayStyle,
          imageSize,
        }),
      )
    }
  }, [imageSize])
  let naturalScale = 1

  if (imageRef.current) {
    const width = parsePixelValue(overlayStyle.width)
    const height = parsePixelValue(overlayStyle.height)
    naturalScale = Math.min(
      imageRef.current.naturalHeight / height,
      imageRef.current.naturalWidth / width,
    )
  }

  return (
    <ModalWindow
      width="medium"
      onClose={onClose}
      header={<Heading size={600}>Edit image</Heading>}
      isOpen
    >
      <div className={styles.croppingTool}>
        <div
          className={styles.croppingToolImageContainer}
          ref={imageContainerRef}
        >
          <img
            crossOrigin="anonymous"
            ref={imageRef}
            onLoad={(event: React.SyntheticEvent<HTMLImageElement>) => {
              const target = event.target as HTMLImageElement
              setImageSize({
                w: target.offsetWidth,
                h: target.offsetHeight,
              })
            }}
            className={styles.croppingToolImage}
            style={{
              width: `${100 * zoom}%`,
              minWidth: `${100 * zoom}%`,
            }}
            src={src}
          />
          <div
            className={styles.croppingToolDragAndDropMove}
            style={{
              maxWidth: `${imageSize.w}px`,
              maxHeight: `${imageSize.h}px`,
            }}
          >
            <DragAndDropMove resize={imageSize.w} onDragEnd={setPosition}>
              <div
                className={clsx(styles.croppingToolOverlay, {
                  [styles.croppingToolOverlayRounded]: rounded,
                })}
                style={overlayStyle}
              />
            </DragAndDropMove>
          </div>
        </div>

        <div className={styles.croppingToolZoomContainer}>
          {zoomOptions && (
            <Zoom
              onChange={setZoom}
              zoom={zoom}
              step={zoomOptions.step}
              min={zoomOptions.min}
              max={zoomOptions.max}
            />
          )}
        </div>

        <Canvas
          rounded={rounded}
          size={{
            w: parsePixelValue(overlayStyle.width),
            h: parsePixelValue(overlayStyle.height),
          }}
          image={imageRef.current}
          imageSize={imageSize}
          imageContainerSize={imageContainerSize}
          position={position}
          onApply={onApply}
          onClose={onClose}
          naturalScale={naturalScale}
          withImageRecommendations={withImageRecommendations}
        />
      </div>
    </ModalWindow>
  )
}

export default CroppingTool
